import { Camera, Download, Paperclip, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../../components/Button';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { ticketErrorRO } from '../ticketCopy';
import {
    useAddAttachmentMutation,
    useDeleteAttachmentMutation,
    useListAttachmentsQuery,
} from '../tickets.api';
import { type Attachment } from '../tickets.schema';

const MAX_BYTES = 20 * 1024 * 1024;

export function AttachmentsPanel({ id }: { id: number }) {
    const { data: attachments, isLoading } = useListAttachmentsQuery(id);
    const [addAttachment, { isLoading: uploading }] = useAddAttachmentMutation();
    const [deleteAttachment] = useDeleteAttachmentMutation();
    const [pendingDelete, setPendingDelete] = useState<Attachment | null>(null);
    const cameraRef = useRef<HTMLInputElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const onFile = (file: File) => {
        if (file.size > MAX_BYTES) {
            toast.error('Fisierul depaseste 20 MB.');
            return;
        }
        const reader = new FileReader();
        reader.onerror = () => toast.error('Nu am putut citi fisierul.');
        reader.onload = async () => {
            try {
                await addAttachment({
                    id,
                    filename: file.name,
                    contentType: file.type || 'application/octet-stream',
                    data: String(reader.result),
                }).unwrap();
                toast.success('Atasament incarcat.');
            } catch (e) {
                toast.error(ticketErrorRO(e, 'Eroare la incarcare.'));
            }
        };
        reader.readAsDataURL(file);
    };

    const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) onFile(f);
        e.target.value = '';
    };

    const onDelete = async () => {
        if (!pendingDelete) return;
        try {
            await deleteAttachment({ id, storageId: pendingDelete.id }).unwrap();
            toast.success('Atasament sters.');
        } catch (e) {
            toast.error(ticketErrorRO(e, 'Eroare la stergere.'));
        } finally {
            setPendingDelete(null);
        }
    };

    const items = attachments ?? [];

    return (
        <div>
            {/* On a phone, "Fa o poza" opens the camera directly (capture); the
                second input is the generic file chooser for desktop / documents. */}
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} />
            <input ref={fileRef} type="file" className="hidden" onChange={onPick} />

            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Atasamente</h2>
                <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" disabled={uploading} onClick={() => cameraRef.current?.click()}>
                        <Camera aria-hidden="true" className="h-4 w-4" />
                        Fa o poza
                    </Button>
                    <Button variant="secondary" disabled={uploading} onClick={() => fileRef.current?.click()}>
                        <Upload aria-hidden="true" className="h-4 w-4" />
                        {uploading ? 'Se incarca...' : 'Incarca fisier'}
                    </Button>
                </div>
            </div>

            {isLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Se incarca...</p>
            ) : items.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Niciun atasament.</p>
            ) : (
                <ul className="flex flex-col gap-2">
                    {items.map((a) => (
                        <li
                            key={a.id}
                            className="flex items-center justify-between gap-3 rounded-xl border border-solid border-gray-200 bg-white px-3 py-2.5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                        >
                            <span className="inline-flex min-w-0 items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                                <Paperclip aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-400" />
                                <span className="truncate">{a.filename || 'atasament'}</span>
                            </span>
                            <span className="flex shrink-0 items-center gap-1">
                                {a.url && (
                                    <Button asChild variant="ghost" className="h-10 w-10 p-0" aria-label="Descarca">
                                        <a href={a.url} target="_blank" rel="noreferrer">
                                            <Download aria-hidden="true" className="h-4 w-4" />
                                        </a>
                                    </Button>
                                )}
                                <Button
                                    variant="ghost"
                                    className="h-10 w-10 p-0"
                                    aria-label="Sterge atasamentul"
                                    onClick={() => setPendingDelete(a)}
                                >
                                    <Trash2 aria-hidden="true" className="h-4 w-4 text-red-600 dark:text-red-400" />
                                </Button>
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            <ConfirmDialog
                open={pendingDelete !== null}
                onClose={() => setPendingDelete(null)}
                onConfirm={onDelete}
                title="Sterge atasamentul?"
                description={pendingDelete ? `"${pendingDelete.filename || 'atasament'}" va fi sters definitiv.` : ''}
                confirmLabel="Sterge"
                variant="danger"
            />
        </div>
    );
}
